import { currencyCodeFor } from "@/components/home/eligibility-currency";
import {
  buildFundingEligibilityIneligibleMessage,
  computeFundingEligibilityEstimate,
  getFundingEligibilityStatus,
  maxSaneMonthlyRevenueFor,
} from "@/components/home/funding-eligibility-shared";
import type { LiveHomeCopy } from "@/content/pages/home/get-live-home-copy";

export interface FundingEligibilityInput {
  monthlyRevenue: number;
  category: string;
  country: string;
}

export interface FundingEligibilityEvaluation {
  formValues: {
    revenue: string;
    category: string;
    yearsInBusiness: string;
    country: string;
  };
  estimatedRange: string | null;
  eligible: boolean;
  ineligibleReason?: string;
}

export function evaluateFundingEligibility(
  input: FundingEligibilityInput,
  locale: string,
  copy: LiveHomeCopy["fundingEligibility"]
): FundingEligibilityEvaluation {
  const revenue = String(input.monthlyRevenue);
  const currency = currencyCodeFor(input.country);
  if (input.monthlyRevenue > maxSaneMonthlyRevenueFor(currency)) {
    throw new Error("Monthly revenue is implausible");
  }

  const formValues = {
    revenue,
    category: input.category,
    // Not collected by this (category-based) eligibility flow — only the
    // restaurant page's time-in-business calculator uses this field.
    yearsInBusiness: "",
    country: input.country,
  };
  const status = getFundingEligibilityStatus(
    revenue,
    input.category,
    input.country
  );
  const eligible = status === "eligible";
  const estimatedRange = eligible
    ? computeFundingEligibilityEstimate(
        revenue,
        currency,
        locale,
        input.country,
        copy.estimatedFundingUpToPrefix,
        input.category
      )
    : null;

  return {
    formValues,
    estimatedRange,
    eligible,
    ...(status === "ineligible"
      ? {
          ineligibleReason: buildFundingEligibilityIneligibleMessage(
            copy.ineligibleMessage,
            revenue,
            currency,
            locale,
            input.country
          ),
        }
      : {}),
  };
}
